import type { Field } from "../../solution/details/tabs/types";
import { ParsedProspect } from "../parser";
import {
  formatProspectValue,
  formatScoreValue,
  formatSubmissionTime,
  getQuestionnaireAnswers,
  hiddenQuestionnaireFields,
  humanizeFieldName,
  isRecord,
  orderScoreKeys,
  scoreFieldPrefix,
  scoreMaxValues,
} from "../prospect-list/model";

export type ProspectDetailsSection = {
  title: string;
  fields: Array<Field>;
};

const overviewQuestionnaireFields = ["entityRole"] as const;

const questionnaireGroups = [
  {
    title: "Project",
    fields: [
      "projectLocation",
      "projectDescription",
      "ecosystemType",
      "projectType",
      "removalPercentage",
      "projectStage",
      "shapefile",
    ],
  },
  {
    title: "Carbon Credits",
    fields: [
      "registry",
      "registryLink",
      "methodology",
      "averageAnnualVolume",
      "vintageVolume",
      "issuanceTiming",
    ],
  },
  {
    title: "Social and Biodiversity",
    fields: [
      "socialDesign",
      "percentageRevenueShare",
      "socialDetail",
      "biodiversityDesignAndCobenefits",
      "biodiversityDetail",
    ],
  },
  {
    title: "Commercial",
    fields: ["commercialIntent", "additionalInfo"],
  },
] as const;

const scoredQuestionnaireFields = new Set<string>([
  "socialDesign",
  "projectStage",
  "projectType",
  "issuanceTiming",
  "averageAnnualVolume",
]);

const groupedQuestionnaireFields = new Set<string>([
  ...overviewQuestionnaireFields,
  ...questionnaireGroups.flatMap((group) => group.fields),
]);

const getAnswerScore = (value: unknown) => {
  return isRecord(value) && typeof value.score === "number"
    ? value.score
    : null;
};

export const getProspectTitle = (prospect: ParsedProspect) => {
  const projectName = formatProspectValue(prospect.projectName);

  return projectName === null || projectName === ""
    ? "Untitled prospect"
    : String(projectName);
};

const toFields = (
  entries: Array<Omit<Field, "index" | "type">>,
): Array<Field> => {
  return entries.map((entry, index) => ({ ...entry, type: "text", index }));
};

const answerField = (field: string, value: unknown) => {
  const score = scoredQuestionnaireFields.has(field)
    ? getAnswerScore(value)
    : null;

  return {
    name: humanizeFieldName(field),
    value: formatProspectValue(value),
    badge:
      score === null
        ? undefined
        : { label: `Score: ${score}`, highlighted: score > 0 },
  };
};

export type ProspectScore = {
  label: string;
  score: number;
  max?: number;
  display: string;
};

export type ProspectScoreSummary = {
  total: ProspectScore;
  categories: Array<ProspectScore>;
};

const toScore = (
  label: string,
  score: number,
  max: number | undefined,
): ProspectScore => ({
  label,
  score,
  max,
  display: formatScoreValue(score, max),
});

export const getProspectScores = (
  prospect: ParsedProspect,
): ProspectScoreSummary => {
  const scores = prospect.scores ?? {};
  const orderedScoreKeys = orderScoreKeys(Object.keys(scores));
  const hasKnownMaxForEveryScore =
    orderedScoreKeys.length > 0 &&
    orderedScoreKeys.every((key) => key in scoreMaxValues);
  const totalMax = hasKnownMaxForEveryScore
    ? orderedScoreKeys.reduce((total, key) => total + scoreMaxValues[key], 0)
    : undefined;

  return {
    total: toScore("Total Score", prospect.totalScores, totalMax),
    categories: orderedScoreKeys.map((key) =>
      toScore(
        humanizeFieldName(`${scoreFieldPrefix}${key}`),
        scores[key],
        scoreMaxValues[key],
      ),
    ),
  };
};

export const buildProspectDetailsSections = (
  prospect: ParsedProspect,
): Array<ProspectDetailsSection> => {
  const answers = getQuestionnaireAnswers(prospect);
  const answerFields = (fields: ReadonlyArray<string>) =>
    fields.map((field) => answerField(field, answers[field]));

  const overview: ProspectDetailsSection = {
    title: "Overview",
    fields: toFields([
      {
        name: "Project Name",
        value: formatProspectValue(prospect.projectName),
      },
      {
        name: "Project Developer",
        value: formatProspectValue(prospect.projectDeveloper),
      },
      { name: "Email", value: formatProspectValue(prospect.email) },
      ...answerFields(overviewQuestionnaireFields),
      {
        name: "Completed at",
        value: formatSubmissionTime(prospect.submissionTime),
      },
      { name: "Submission ID", value: prospect.submissionId },
    ]),
  };

  const sections: Array<ProspectDetailsSection> = [
    overview,
    ...questionnaireGroups.map((group) => ({
      title: group.title,
      fields: toFields(answerFields(group.fields)),
    })),
  ];

  const otherAnswerFields = Object.keys(answers).filter(
    (field) =>
      !groupedQuestionnaireFields.has(field) &&
      !hiddenQuestionnaireFields.has(field),
  );
  if (otherAnswerFields.length) {
    sections.push({
      title: "Other Answers",
      fields: toFields(answerFields(otherAnswerFields)),
    });
  }

  return sections;
};

export const getPrestoneReportUrl = (prestoneId: string) => {
  const reportUrl = import.meta.env.VITE_PRESTONE_REPORT_URL;
  if (!reportUrl) {
    return null;
  }

  const url = new URL(reportUrl);
  url.searchParams.set("prestoneId", prestoneId);
  return url.toString();
};
